import { TIER_LIMITS, type Tier } from '../constants';

/**
 * Calculate UTF-8 byte length of a string without requiring TextEncoder.
 * Works in all JavaScript environments.
 */
function getUtf8ByteLength(str: string): number {
  let bytes = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // Surrogate pair (4 bytes for the pair)
      bytes += 4;
      i++; // Skip the low surrogate
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

export interface ValidationError {
  error: string;
  code: string;
  field: string;
  limit?: number;
  actual?: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

interface EndpointInput {
  path?: string;
  contentType?: string;
  responseBody?: string;
  statusCode?: number;
  delay?: number;
  delayMax?: number;
  rateLimit?: number;
}

/**
 * Validates endpoint creation/update data against tier limits.
 * Returns validation result with detailed errors.
 */
export function validateEndpointInput(
  input: EndpointInput,
  tier: Tier,
  options: { requirePath?: boolean } = {}
): ValidationResult {
  const errors: ValidationError[] = [];
  const limits = TIER_LIMITS[tier];

  // Path validation (required for creation)
  if (options.requirePath) {
    if (!input.path || typeof input.path !== 'string') {
      errors.push({
        error: 'Path is required',
        code: 'PATH_REQUIRED',
        field: 'path',
      });
    } else if (!input.path.startsWith('/')) {
      errors.push({
        error: 'Path must start with /',
        code: 'PATH_INVALID',
        field: 'path',
      });
    }
  }

  // Response body size validation
  if (input.responseBody !== undefined) {
    const bodySize = getUtf8ByteLength(input.responseBody);
    if (bodySize > limits.maxResponseSize) {
      errors.push({
        error: `Response body exceeds ${tier} tier limit of ${formatBytes(limits.maxResponseSize)}`,
        code: 'RESPONSE_BODY_TOO_LARGE',
        field: 'responseBody',
        limit: limits.maxResponseSize,
        actual: bodySize,
      });
    }
  }

  // Status code validation (valid HTTP status codes: 100-599)
  if (input.statusCode !== undefined) {
    if (
      typeof input.statusCode !== 'number' ||
      !Number.isInteger(input.statusCode) ||
      input.statusCode < 100 ||
      input.statusCode > 599
    ) {
      errors.push({
        error: 'Status code must be a valid HTTP status (100-599)',
        code: 'STATUS_CODE_INVALID',
        field: 'statusCode',
      });
    }
  }

  // Delay validation
  if (input.delay !== undefined) {
    if (typeof input.delay !== 'number' || input.delay < 0) {
      errors.push({
        error: 'Delay must be a non-negative number',
        code: 'DELAY_INVALID',
        field: 'delay',
      });
    } else if (input.delay > limits.maxDelay) {
      errors.push({
        error: `Delay exceeds ${tier} tier limit of ${limits.maxDelay}ms`,
        code: 'DELAY_TOO_LONG',
        field: 'delay',
        limit: limits.maxDelay,
        actual: input.delay,
      });
    }
  }

  // Delay max validation
  if (input.delayMax !== undefined) {
    if (typeof input.delayMax !== 'number' || input.delayMax < 0) {
      errors.push({
        error: 'Delay max must be a non-negative number',
        code: 'DELAY_MAX_INVALID',
        field: 'delayMax',
      });
    } else if (input.delayMax > limits.maxDelay) {
      errors.push({
        error: `Delay max exceeds ${tier} tier limit of ${limits.maxDelay}ms`,
        code: 'DELAY_MAX_TOO_LONG',
        field: 'delayMax',
        limit: limits.maxDelay,
        actual: input.delayMax,
      });
    }
  }

  // Rate limit validation
  if (input.rateLimit !== undefined) {
    if (typeof input.rateLimit !== 'number' || !Number.isInteger(input.rateLimit) || input.rateLimit < 1) {
      errors.push({
        error: 'Rate limit must be a positive integer',
        code: 'RATE_LIMIT_INVALID',
        field: 'rateLimit',
      });
    } else if (input.rateLimit > limits.maxEndpointRateLimit) {
      errors.push({
        error: `Rate limit exceeds ${tier} tier maximum of ${limits.maxEndpointRateLimit} requests/minute`,
        code: 'RATE_LIMIT_EXCEEDS_TIER',
        field: 'rateLimit',
        limit: limits.maxEndpointRateLimit,
        actual: input.rateLimit,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}

/**
 * Guards against open redirects. Used for post-auth return paths (e.g. an invite
 * link) that get round-tripped through magic-link/OAuth state stored server-side.
 * Only a same-origin relative path is allowed through; anything that could send
 * the browser to another host is dropped.
 */
export function safeRedirectPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (!value.startsWith('/')) return null;
  // `//host/path` and `/\host/path` are protocol-relative — the browser treats
  // them as external. A literal `://` anywhere means an absolute URL slipped in
  // (e.g. `/redirect?url=https://evil.com` still contains `://`).
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  if (value.includes('://')) return null;
  return value;
}

// Passwords are 8 to 128 characters with no composition rules (platform
// standards §6). The API checks the cap before any hashing, everywhere a
// password is accepted; the forms check it too, to answer sooner.
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** The error to show for a password of the wrong length, or null when it's fine. */
export function passwordLengthError(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
  }
  return null;
}
